import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Building2, CreditCard, Landmark, Plus, Save, Pencil, Trash2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BANK_ACCOUNT_TYPES = ['Current', 'Savings', 'NRO', 'NRE', 'OD'];
const CARD_NETWORKS = ['Visa', 'Mastercard', 'RuPay', 'AMEX', 'Diners'];

function AccountCard({ account, onEdit, onDelete, onView }) {
  const isCC = account.card_type === 'credit_card';
  const isClosed = account.status === 'closed' || !!account.active_until;
  return (
    <div className={`bg-white border rounded-lg p-4 transition-all hover:shadow-sm ${isClosed ? 'border-[#D5D2C6] opacity-60' : 'border-[#E2E0D8]'}`}
      data-testid={`biz-account-card-${account.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {isCC ? <CreditCard size={16} className="text-amber-500" /> : <Landmark size={16} className="text-blue-500" />}
          <span className="font-medium text-sm text-[#1C1C19]">{account.card_name || account.issuer || 'Unknown'}</span>
          {isClosed && <span className="text-[9px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-medium">CLOSED</span>}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onEdit(account)} className="text-[#B5B3A9] hover:text-[#284A33] transition-colors p-1" data-testid={`biz-edit-${account.id}`}>
            <Pencil size={12} />
          </button>
          <button onClick={() => onDelete(account)} className="text-[#B5B3A9] hover:text-red-500 transition-colors p-1" data-testid={`biz-delete-${account.id}`}>
            <Trash2 size={12} />
          </button>
        </div>
      </div>
      <div className="space-y-1.5 text-xs text-[#5C5C55]">
        {account.issuer && <div>Issuer: <span className="text-[#1C1C19] font-medium">{account.issuer}</span></div>}
        {account.last_four && <div>{isCC ? 'Card' : 'A/c'}: <span className="font-mono text-[#1C1C19]">••••{account.last_four}</span></div>}
        {account.account_number && <div>A/c: <span className="font-mono text-[#1C1C19]">{account.account_number}</span></div>}
        <div className="flex gap-4 pt-1.5 border-t border-[#E2E0D8] mt-2">
          <span>{account.statement_count || 0} statements</span>
          <span>{account.transaction_count || 0} transactions</span>
        </div>
      </div>
    </div>
  );
}

export default function BusinessAccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('bank');
  const [dialog, setDialog] = useState({ open: false, mode: 'add', data: {} });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, account: null });
  const navigate = useNavigate();

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/business/accounts`, { withCredentials: true });
      setAccounts(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const isBankTab = tab === 'bank';
  const filtered = accounts.filter(a => isBankTab ? a.card_type === 'bank_account' : a.card_type === 'credit_card');

  const openAdd = () => {
    setDialog({
      open: true, mode: 'add',
      data: {
        card_type: isBankTab ? 'bank_account' : 'credit_card',
        issuer: '', card_name: '', last_four: '', region: 'india',
        account_number: '', account_type: isBankTab ? 'Current' : '',
        account_category: 'business',
      }
    });
  };

  const openEdit = (a) => setDialog({ open: true, mode: 'edit', data: { ...a } });

  const saveAccount = async () => {
    const d = dialog.data;
    try {
      if (dialog.mode === 'add') {
        await axios.post(`${API}/cards`, { ...d, account_category: 'business' }, { withCredentials: true });
        toast.success(`Business ${isBankTab ? 'bank account' : 'credit card'} added`);
      } else {
        const { id, user_id, statement_count, transaction_count, created_at, ...updates } = d;
        await axios.patch(`${API}/cards/${d.id}`, { ...updates, account_category: 'business' }, { withCredentials: true });
        toast.success('Updated');
      }
      setDialog({ open: false, mode: 'add', data: {} });
      fetchAccounts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const deleteAccount = async (keepTxns) => {
    const a = deleteDialog.account;
    try {
      await axios.delete(`${API}/cards/${a.id}?delete_transactions=${!keepTxns}`, { withCredentials: true });
      toast.success('Deleted');
      setDeleteDialog({ open: false, account: null });
      fetchAccounts();
    } catch { toast.error('Delete failed'); }
  };

  const updateField = (key, val) => setDialog(d => ({ ...d, data: { ...d.data, [key]: val } }));

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="business-accounts-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-[#1C1C19]">Business Accounts</h2>
          <p className="text-sm text-[#5C5C55] mt-1">Manage business bank accounts and credit cards</p>
        </div>
        <Button onClick={openAdd} data-testid="biz-add-account-btn"
          className="bg-[#284A33] text-white hover:bg-[#1C3624] text-xs h-8 px-3">
          <Plus size={13} className="mr-1.5" /> Add {isBankTab ? 'Bank Account' : 'Credit Card'}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex items-center border-b border-[#E2E0D8]" data-testid="biz-accounts-tabs">
        <button onClick={() => setTab('bank')} data-testid="biz-tab-bank"
          className={`px-5 py-2.5 text-sm font-medium transition-colors relative flex items-center gap-2 ${tab === 'bank' ? 'text-[#284A33]' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
          <Landmark size={15} /> Bank Accounts
          <span className="text-[10px] bg-[#E8E6DE] text-[#5C5C55] px-1.5 py-0.5 rounded-full font-mono">{accounts.filter(a => a.card_type === 'bank_account').length}</span>
          {tab === 'bank' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#284A33] rounded-t" />}
        </button>
        <button onClick={() => setTab('card')} data-testid="biz-tab-card"
          className={`px-5 py-2.5 text-sm font-medium transition-colors relative flex items-center gap-2 ${tab === 'card' ? 'text-[#284A33]' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
          <CreditCard size={15} /> Credit Cards
          <span className="text-[10px] bg-[#E8E6DE] text-[#5C5C55] px-1.5 py-0.5 rounded-full font-mono">{accounts.filter(a => a.card_type === 'credit_card').length}</span>
          {tab === 'card' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#284A33] rounded-t" />}
        </button>
      </div>

      {/* Account Cards */}
      {loading ? (
        <div className="text-center py-12 text-[#5C5C55] text-sm">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <div className={`w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center ${isBankTab ? 'bg-blue-50' : 'bg-amber-50'}`}>
            {isBankTab ? <Landmark size={24} className="text-blue-400" /> : <CreditCard size={24} className="text-amber-400" />}
          </div>
          <p className="text-sm text-[#5C5C55] mb-1">No business {isBankTab ? 'bank accounts' : 'credit cards'} added yet</p>
          <p className="text-xs text-[#5C5C55]/60">Click "Add" to register a business account</p>
        </div>
      ) : (() => {
        const active = filtered.filter(a => a.status !== 'closed' && !a.active_until);
        const closed = filtered.filter(a => a.status === 'closed' || a.active_until);
        return (
          <div className="space-y-6">
            {active.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {active.map(a => (
                  <AccountCard key={a.id} account={a} onEdit={openEdit}
                    onDelete={(a) => setDeleteDialog({ open: true, account: a })} />
                ))}
              </div>
            )}
            {closed.length > 0 && (
              <>
                {active.length > 0 && (
                  <div className="flex items-center gap-3">
                    <div className="h-px flex-1 bg-[#E2E0D8]" /><span className="text-[10px] uppercase tracking-widest text-[#5C5C55]/60 font-medium">Closed</span><div className="h-px flex-1 bg-[#E2E0D8]" />
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 opacity-60">
                  {closed.map(a => (
                    <AccountCard key={a.id} account={a} onEdit={openEdit}
                      onDelete={(a) => setDeleteDialog({ open: true, account: a })} />
                  ))}
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* Add/Edit Dialog */}
      <Dialog open={dialog.open} onOpenChange={(o) => !o && setDialog({ ...dialog, open: false })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {dialog.data.card_type === 'bank_account' ? <Landmark size={16} className="text-blue-600" /> : <CreditCard size={16} className="text-amber-600" />}
              {dialog.mode === 'add' ? 'Add' : 'Edit'} Business {dialog.data.card_type === 'bank_account' ? 'Bank Account' : 'Credit Card'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {dialog.data.card_type === 'bank_account' ? (
              <>
                <div>
                  <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">Bank Name *</Label>
                  <Input data-testid="biz-field-issuer" value={dialog.data.issuer || ''} onChange={e => updateField('issuer', e.target.value)}
                    placeholder="e.g. ICICI" className="border-[#E2E0D8]" />
                </div>
                <div>
                  <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">Account Name</Label>
                  <Input data-testid="biz-field-card-name" value={dialog.data.card_name || ''} onChange={e => updateField('card_name', e.target.value)}
                    placeholder="e.g. ICICI Business Current" className="border-[#E2E0D8]" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">Account Number *</Label>
                    <Input data-testid="biz-field-account-number" value={dialog.data.account_number || ''} onChange={e => updateField('account_number', e.target.value)}
                      placeholder="e.g. 12345678" className="border-[#E2E0D8]" />
                  </div>
                  <div>
                    <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">Account Type</Label>
                    <Select value={dialog.data.account_type || 'Current'} onValueChange={v => updateField('account_type', v)}>
                      <SelectTrigger data-testid="biz-field-account-type" className="border-[#E2E0D8]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {BANK_ACCOUNT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">Card Issuer *</Label>
                  <Input data-testid="biz-field-issuer" value={dialog.data.issuer || ''} onChange={e => updateField('issuer', e.target.value)}
                    placeholder="e.g. ICICI" className="border-[#E2E0D8]" />
                </div>
                <div>
                  <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">Card Name</Label>
                  <Input data-testid="biz-field-card-name" value={dialog.data.card_name || ''} onChange={e => updateField('card_name', e.target.value)}
                    placeholder="e.g. ICICI Emirates" className="border-[#E2E0D8]" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">Last 4 Digits *</Label>
                    <Input data-testid="biz-field-last-four" value={dialog.data.last_four || ''} onChange={e => updateField('last_four', e.target.value)}
                      placeholder="e.g. 5007" maxLength={4} className="border-[#E2E0D8]" />
                  </div>
                  <div>
                    <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">Network</Label>
                    <Select value={dialog.data.account_type || ''} onValueChange={v => updateField('account_type', v)}>
                      <SelectTrigger data-testid="biz-field-network" className="border-[#E2E0D8]"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {CARD_NETWORKS.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}
            {/* Lifecycle dates */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">Started On</Label>
                <Input data-testid="biz-field-active-from" type="month" value={dialog.data.active_from || ''}
                  onChange={e => updateField('active_from', e.target.value)} className="border-[#E2E0D8]" />
              </div>
              <div>
                <Label className="text-[#5C5C55] text-xs uppercase tracking-wider mb-1 block">Closed On</Label>
                <Input data-testid="biz-field-active-until" type="month" value={dialog.data.active_until || ''}
                  onChange={e => updateField('active_until', e.target.value)} className="border-[#E2E0D8]" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog({ ...dialog, open: false })}>Cancel</Button>
            <Button onClick={saveAccount} data-testid="biz-save-account-btn" className="bg-[#284A33] text-white hover:bg-[#1C3624]">
              <Save size={13} className="mr-1.5" /> {dialog.mode === 'add' ? 'Add' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialog.open} onOpenChange={(o) => !o && setDeleteDialog({ open: false, account: null })}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600">Delete {deleteDialog.account?.card_name || deleteDialog.account?.issuer}?</DialogTitle>
            <DialogDescription>What should happen to linked statements and transactions?</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Button onClick={() => deleteAccount(true)} variant="outline" className="w-full justify-start text-sm" data-testid="biz-delete-keep-txns">
              Keep transactions, remove account only
            </Button>
            <Button onClick={() => deleteAccount(false)} variant="destructive" className="w-full justify-start text-sm" data-testid="biz-delete-all">
              Delete everything (account + statements + transactions)
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteDialog({ open: false, account: null })}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
